/**
 * Lite theme — Cart store.
 *
 * Each entry now carries the full ordering configuration so the cart page can
 * render product details, recompute totals and send a complete WhatsApp order
 * payload without re-opening the configurator.
 *
 * A composite `key` (device|variant|grade|screen|color|sub) uniquely identifies
 * an item; adding the same configuration twice increments quantity instead of
 * creating a duplicate row. Legacy entries (id-only) remain readable and are
 * automatically promoted to the new shape on first interaction.
 */
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { useLiteFavorites, registerCartSnapshotProvider } from "./liteFavoritesStore";
import { getCurrentTenantId } from "@/lib/tenantContext";
import { logCustomerActivity } from "@/data/customerActivity";

const LS_KEY_BASE = "khays_lite_cart";
function lsKey() {
  const t = getCurrentTenantId();
  return t ? `${LS_KEY_BASE}__t_${t}` : LS_KEY_BASE;
}

export interface LiteCartItem {
  /** Composite key (device|variant|grade|screen|color|sub) for uniqueness. */
  key: string;
  /** Stable product identifier — device.name (kept for legacy compatibility). */
  id: string;
  quantity: number;
  addedAt: number;

  // ─── Configuration snapshot ───
  deviceName: string;
  displayName: string;
  category: string;
  variant?: string;
  grade?: string;
  screenType?: string;
  color?: string;
  /** Map of sub-variant id → option label. */
  subVariants?: Record<string, string>;
  /** Joined sub-variant suffix (option labels) used in the WhatsApp payload. */
  subVariantSummary?: string;
  /** Per-unit price in GHS (already includes margin / extra profit). */
  unitPrice: number;
  /** Pre-discount per-unit price in GHS (only set when a discount applies). */
  originalUnitPrice?: number;
  /** Discount percent applied to this line item (e.g. 10 for 10% OFF). */
  discountPercent?: number;
  /** Featured image URL (best effort) for cart card thumbnail. */
  imageUrl?: string;
  /**
   * Shipping option id pre-selected from the configurator (if the device only
   * has one shipping option, this is locked at add-to-cart time).
   */
  shippingOptionId?: string;
}

export function buildCartKey(parts: {
  deviceName: string;
  variant?: string;
  grade?: string;
  screenType?: string;
  color?: string;
  subVariantSummary?: string;
}): string {
  return [
    parts.deviceName,
    parts.variant || "",
    parts.grade || "",
    parts.screenType || "",
    parts.color || "",
    parts.subVariantSummary || "",
  ].join("|");
}

interface LiteCartState {
  items: LiteCartItem[];
  _rev: number;
  count: () => number;
  total: () => number;
  /**
   * Add an item to the cart. Accepts either a full configured payload (object)
   * or a legacy device-name string (still used by older code paths). Same
   * configuration → quantity increments.
   */
  add: (input: string | Omit<LiteCartItem, "key" | "quantity" | "addedAt"> & { quantity?: number }) => void;
  remove: (key: string) => void;
  setQty: (key: string, qty: number) => void;
  clear: () => void;
  reload: () => void;
}

function loadLocal(): LiteCartItem[] {
  try {
    const raw = localStorage.getItem(lsKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    // Promote legacy entries ({id, quantity, addedAt}) to the new shape so the
    // UI doesn't crash on missing fields.
    return parsed.map((entry) => {
      if (entry && typeof entry === "object" && "key" in entry) return entry as LiteCartItem;
      const id = entry?.id || "unknown";
      return {
        key: buildCartKey({ deviceName: id }),
        id,
        deviceName: id,
        displayName: id,
        category: "",
        quantity: entry?.quantity || 1,
        addedAt: entry?.addedAt || Date.now(),
        unitPrice: 0,
      } as LiteCartItem;
    });
  } catch {
    return [];
  }
}

function persistLocal(items: LiteCartItem[]) {
  try { localStorage.setItem(lsKey(), JSON.stringify(items)); } catch {}
}

function syncCloud(items: LiteCartItem[]) {
  const uid = useLiteFavorites.getState().userId;
  if (!uid) return;
  const favs = useLiteFavorites.getState().items;
  supabase
    .from("app_data")
    .upsert(
      { data_key: `khays_lite_user_${uid}`, data_value: { favorites: favs, cart: items } as any },
      { onConflict: "data_key" }
    )
    .then(({ error }) => { if (error) console.warn("Lite cart cloud sync failed", error); });
}

export const useLiteCart = create<LiteCartState>((set, get) => ({
  items: loadLocal(),
  _rev: 0,

  count: () => get().items.reduce((s, i) => s + i.quantity, 0),
  total: () => get().items.reduce((s, i) => s + i.unitPrice * i.quantity, 0),

  add: (input) => {
    // Legacy string call → minimal entry (display will fall back to device name).
    const payload: Omit<LiteCartItem, "key" | "quantity" | "addedAt"> & { quantity?: number } =
      typeof input === "string"
        ? {
            id: input,
            deviceName: input,
            displayName: input,
            category: "",
            unitPrice: 0,
          }
        : input;

    const key = buildCartKey({
      deviceName: payload.deviceName,
      variant: payload.variant,
      grade: payload.grade,
      screenType: payload.screenType,
      color: payload.color,
      subVariantSummary: payload.subVariantSummary,
    });
    const qtyToAdd = payload.quantity ?? 1;

    const existing = get().items.find(i => i.key === key);
    let next: LiteCartItem[];
    if (existing) {
      next = get().items.map(i =>
        i.key === key ? { ...i, quantity: i.quantity + qtyToAdd, unitPrice: payload.unitPrice || i.unitPrice } : i
      );
    } else {
      next = [
        ...get().items,
        {
          ...payload,
          key,
          quantity: qtyToAdd,
          addedAt: Date.now(),
        } as LiteCartItem,
      ];
    }
    persistLocal(next);
    set({ items: next, _rev: get()._rev + 1 });
    try { void logCustomerActivity("cart_add", { id: payload.id, key, qty: qtyToAdd, unitPrice: payload.unitPrice }); } catch {}
    syncCloud(next);
  },

  remove: (key) => {
    const removed = get().items.find(i => i.key === key);
    const next = get().items.filter(i => i.key !== key);
    persistLocal(next);
    set({ items: next, _rev: get()._rev + 1 });
    try { void logCustomerActivity("cart_remove", { id: removed?.id, key }); } catch {}
    syncCloud(next);
  },

  setQty: (key, qty) => {
    if (qty <= 0) { get().remove(key); return; }
    const next = get().items.map(i => i.key === key ? { ...i, quantity: qty } : i);
    persistLocal(next);
    set({ items: next, _rev: get()._rev + 1 });
    syncCloud(next);
  },

  clear: () => {
    persistLocal([]);
    set({ items: [], _rev: get()._rev + 1 });
    syncCloud([]);
  },

  reload: () => set({ items: loadLocal(), _rev: get()._rev + 1 }),
}));

// Wire the snapshot provider so favorites' cloud pushes include the cart.
registerCartSnapshotProvider(() => useLiteCart.getState().items);

// Reload cart + favorites from the (now tenant-namespaced) localStorage key
// whenever the active tenant scope changes.
import { useTenantStore } from "@/lib/tenantContext";
let _lastTenantScope: string | null | undefined = undefined;
useTenantStore.subscribe((s) => {
  if (_lastTenantScope === undefined) { _lastTenantScope = s.tenantId; return; }
  if (s.tenantId !== _lastTenantScope) {
    _lastTenantScope = s.tenantId;
    try { useLiteCart.getState().reload(); } catch {}
    try { useLiteFavorites.getState().reload(); } catch {}
  }
});
